import { KPIRepository } from '../repository/KPIRepository';
import { StageRepository } from '../repository/StageRepository';
import type { Category } from '../repository/StageRepository';

/**
 * Service for CRM related operations that span multiple repositories
 */
export class CrmService {
  private kpiRepository: typeof KPIRepository;
  private stageRepository: typeof StageRepository;

  /**
   * Create a new CrmService
   * @param kpiRepository - Optional KPI repository instance (for testing)
   * @param stageRepository - Optional Stage repository instance (for testing)
   */
  constructor(
    kpiRepository = KPIRepository,
    stageRepository = StageRepository
  ) {
    this.kpiRepository = kpiRepository;
    this.stageRepository = stageRepository;
  }

  /**
   * Fetch stage categories with dependent KPI rules for each stage
   * @param orgID - Organization ID
   * @returns Categories with populated dependent KPI rules for stages
   */
  async getCategoriesWithDependentKPIs(orgID: string): Promise<Category[]> {
    // Get categories with stages
    const categories = (await this.stageRepository.fetchStageCategories(
      orgID
    )) as unknown as Category[];

    // Get all KPI rules for this organization that have sourceType = 'stage'
    const allKpiRules = await this.kpiRepository.fetchKPIRules(orgID);
    const stageRules = allKpiRules.filter(
      (rule) => rule.sourceType === 'stage'
    );
    // For each category, find the dependent KPI rules
    for (const category of categories) {
      // Initialize dependentKPIRules as a dictionary
      const dependentRulesMap: Record<string, string[]> = {};

      // Extract stages array from the category
      const stages = Array.isArray(category.stages)
        ? category.stages
        : category.stages?.items || [];

      // Process each stage
      stages.forEach((stage) => {
        // Find rules that depend on this specific stage
        const rulesForStage = stageRules
          .filter((rule) => rule.triggerID === stage.id)
          .map((rule) => rule.title);

        // Only add to map if there are dependent rules
        if (rulesForStage.length > 0) {
          dependentRulesMap[stage.id] = rulesForStage;
        }
      });
      // Add the map to the category
      category.dependentKPIRules = dependentRulesMap;
    }

    return categories;
  }
}

export default CrmService;
